/**
 * What is currently being highlighted on the page, and why.
 *
 * A module store rather than React context or a URL parameter, for one
 * reason: the search panel and the thing it opens have no common ancestor
 * either host could hold state on. Clicking a result navigates — a route
 * change on the platform, a route change in the runtime — and the panel
 * unmounts before the target has finished loading. A store outlives both.
 *
 * It is mirrored into `sessionStorage` so a reload of a result's deep link
 * still lands with the terms highlighted, and it is per-tab: two tabs
 * searching for different things must not fight over one highlight.
 */

import { useSyncExternalStore } from 'react'

const KEY = 'twasta.search.highlight'

export interface ActiveHighlight {
  /** What the reader typed — shown on the match bar. */
  query: string
  /** The terms the SERVER matched on, so the page highlights what the result
   *  list highlighted rather than a second, client-side interpretation. */
  terms: string[]
  /** Human label for the target, e.g. "Purchase Orders" — bar context only. */
  label?: string
}

function read(): ActiveHighlight | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ActiveHighlight
    if (!parsed || !Array.isArray(parsed.terms) || !parsed.terms.length) return null
    return parsed
  } catch {
    return null
  }
}

function write(value: ActiveHighlight | null): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    if (value) sessionStorage.setItem(KEY, JSON.stringify(value))
    else sessionStorage.removeItem(KEY)
  } catch {
    // A browser refusing session storage (private mode quota, blocked site
    // data) costs the reload-survives-highlight nicety and nothing else.
  }
}

let current: ActiveHighlight | null = read()
const listeners = new Set<() => void>()

export function setActiveHighlight(value: ActiveHighlight | null): void {
  const next = value && value.terms?.length ? value : null
  current = next
  write(next)
  listeners.forEach((fn) => fn())
}

export function getActiveHighlight(): ActiveHighlight | null {
  return current
}

export function clearActiveHighlight(): void {
  setActiveHighlight(null)
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

/** Server snapshot is always null: there is no highlight before hydration. */
export function useActiveHighlight(): ActiveHighlight | null {
  return useSyncExternalStore(subscribe, getActiveHighlight, () => null)
}
